import React, { useReducer, useCallback } from 'react';
import {
	createSubCategoryInitialState,
	CreateSubCategoryContext,
	TVisibleForm,
	TTypeOfModalWindow,
} from './createSubCategoryContext';
import { createSubCategoryReducer } from './createSubCategoryReducer';
import { CreateSubCategoryActionTypes } from './createSubCategoryActions';
import {
	ICreateSubCategoryValidationError,
	IItemForGetShortInfo,
	IItemForGetShortInfoWithStatus,
	IServerError,
	TFileId,
	TLanguageIDs,
	IDefaultLanguage,
	ICreateSubCategory,
} from 'types';

const CreateSubCategoryState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(createSubCategoryReducer, createSubCategoryInitialState);

	const changeVisibleForm = useCallback((value: TVisibleForm) => {
		dispatch({
			type: CreateSubCategoryActionTypes.CHANGE_VISIBLE_FORM,
			payload: {
				visibleForm: value,
			},
		});
	}, []);

	const setFileId = useCallback((value: TFileId) => {
		dispatch({
			type: CreateSubCategoryActionTypes.SET_FILE_ID,
			payload: {
				fileId: value,
			},
		});
	}, []);

	const setFetchedCustomers = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateSubCategoryActionTypes.SET_FETCHED_CUSTOMERS,
			payload: {
				customers: value,
			},
		});
	}, []);

	const setIDCustomer = useCallback((value: Nullable<string>) => {
		dispatch({
			type: CreateSubCategoryActionTypes.SET_ID_CUSTOMER,
			payload: {
				IDSelectedCustomer: value,
			},
		});
	}, []);

	const setFetchedRestaurants = useCallback((value: IItemForGetShortInfoWithStatus[]) => {
		dispatch({
			type: CreateSubCategoryActionTypes.SET_FETCHED_RESTAURANTS,
			payload: {
				restaurants: value,
			},
		});
	}, []);

	const setIDsRestaurant = useCallback(
		(value: string[]) => {
			const updateIDsOfRestaurantsWhereToHide = state.IDsOfRestaurantsWhereToHide.filter((el) =>
				value.includes(el),
			);
			const updateIDsOfRestaurantsAvailableForRepetition = state.IDsOfRestaurantsAvailableForRepetition.filter(
				(el) => value.includes(el),
			);
			dispatch({
				type: CreateSubCategoryActionTypes.SET_IDS_RESTAURANT,
				payload: {
					IDsSelectedRestaurant: value,
					IDsOfRestaurantsWhereToHide: updateIDsOfRestaurantsWhereToHide,
					IDsOfRestaurantsAvailableForRepetition: updateIDsOfRestaurantsAvailableForRepetition,
				},
			});
		},
		[state.IDsOfRestaurantsWhereToHide, state.IDsOfRestaurantsAvailableForRepetition],
	);

	const changeNameOfSubCategory = useCallback((value: string) => {
		dispatch({
			type: CreateSubCategoryActionTypes.CHANGE_NAME_OF_SUBCATEGORY,
			payload: {
				nameOfSubCategory: value,
			},
		});
	}, []);

	const setFetchedCategories = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateSubCategoryActionTypes.SET_FETCHED_CATEGORIES,
			payload: {
				categories: value,
			},
		});
	}, []);

	const setIDCategory = useCallback((value: Nullable<string>) => {
		dispatch({
			type: CreateSubCategoryActionTypes.SET_ID_CATEGORY,
			payload: {
				IDSelectedCategory: value,
			},
		});
	}, []);

	const setFetchedTranslations = useCallback(
		(value: TLanguageIDs[], defaultCustomerLanguage: Nullable<IDefaultLanguage>) => {
			const translations = value.map((id) => ({ languageId: id, name: '' }));
			dispatch({
				type: CreateSubCategoryActionTypes.SET_FETCHED_TRANSLATIONS,
				payload: {
					translations,
					defaultCustomerLanguage,
				},
			});
		},
		[],
	);

	const setTranslationTab = useCallback((value: TLanguageIDs | null) => {
		dispatch({
			type: CreateSubCategoryActionTypes.SET_TRANSLATION_TAB,
			payload: {
				selectedTranslationTab: value,
			},
		});
	}, []);

	const changeNameTranslation = useCallback(
		(languageId: TLanguageIDs, value: string) => {
			const updatedTranslations = state.translations.map((translation) =>
				translation.languageId === languageId
					? {
							...translation,
							name: value,
					  }
					: translation,
			);
			dispatch({
				type: CreateSubCategoryActionTypes.CHANGE_TRANSLATION,
				payload: {
					translations: updatedTranslations,
				},
			});
		},
		[state.translations],
	);

	const setDataForSubmit = useCallback((value: ICreateSubCategory | null) => {
		dispatch({
			type: CreateSubCategoryActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const setIDsOfRestaurantsWhereToHide = useCallback((value: string[]) => {
		dispatch({
			type: CreateSubCategoryActionTypes.SET_IDS_OF_RESTAURANTS_WHERE_TO_HIDE,
			payload: {
				IDsOfRestaurantsWhereToHide: value,
			},
		});
	}, []);

	const setIDsOfRestaurantsAvailableForRepetition = useCallback((value: string[]) => {
		dispatch({
			type: CreateSubCategoryActionTypes.SET_IDS_OF_RESTAURANTS_AVAILABLE_FOR_REPETITION,
			payload: {
				IDsOfRestaurantsAvailableForRepetition: value,
			},
		});
	}, []);

	const changeActivationSchedule = useCallback((value: string) => {
		dispatch({
			type: CreateSubCategoryActionTypes.CHANGE_ACTIVATION_SCHEDULE,
			payload: {
				activationSchedule: value,
			},
		});
	}, []);

	const changeDeactivationSchedule = useCallback((value: string) => {
		dispatch({
			type: CreateSubCategoryActionTypes.CHANGE_DEACTIVATION_SCHEDULE,
			payload: {
				deactivationSchedule: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: CreateSubCategoryActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const showErrorModalWithInfo = useCallback((value: IServerError<ICreateSubCategoryValidationError> | null) => {
		dispatch({
			type: CreateSubCategoryActionTypes.SHOW_ERROR_MODAL_WITH_INFO,
			payload: {
				errorResponseData: value,
			},
		});
	}, []);

	const resetFields = useCallback(() => dispatch({ type: CreateSubCategoryActionTypes.RESET_FIELDS }), []);

	return (
		<CreateSubCategoryContext.Provider
			value={{
				changeVisibleForm,
				setFileId,
				setFetchedCustomers,
				setIDCustomer,
				setFetchedRestaurants,
				setIDsRestaurant,
				changeNameOfSubCategory,
				setFetchedCategories,
				setIDCategory,
				setIDsOfRestaurantsWhereToHide,
				setIDsOfRestaurantsAvailableForRepetition,
				changeActivationSchedule,
				changeDeactivationSchedule,
				setFetchedTranslations,
				setTranslationTab,
				changeNameTranslation,
				setDataForSubmit,
				changeTypeOfModalWindow,
				showErrorModalWithInfo,
				resetFields,
				visibleForm: state.visibleForm,
				fileId: state.fileId,
				fetchedCustomers: state.fetchedCustomers,
				IDSelectedCustomer: state.IDSelectedCustomer,
				fetchedRestaurants: state.fetchedRestaurants,
				IDsSelectedRestaurant: state.IDsSelectedRestaurant,
				nameOfSubCategory: state.nameOfSubCategory,
				fetchedCategories: state.fetchedCategories,
				IDSelectedCategory: state.IDSelectedCategory,
				IDsOfRestaurantsWhereToHide: state.IDsOfRestaurantsWhereToHide,
				IDsOfRestaurantsAvailableForRepetition: state.IDsOfRestaurantsAvailableForRepetition,
				activationSchedule: state.activationSchedule,
				deactivationSchedule: state.deactivationSchedule,
				defaultCustomerLanguage: state.defaultCustomerLanguage,
				selectedTranslationTab: state.selectedTranslationTab,
				translations: state.translations,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
				errorResponse: state.errorResponse,
			}}>
			{children}
		</CreateSubCategoryContext.Provider>
	);
};

export default CreateSubCategoryState;
